import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import { useMemo } from "react";

import { useTran } from "@/hooks";
import { MultiSelect } from "@/components/common";
import { LOCATION_OPTIONS, KEY_IMPROVEMENT } from "@/lib/constants";
import {
  translateCountry,
  OBJECTIVE_NAME_MAP,
  translateValue,
} from "@/lib/constants/translations.map";

const CompanySetupStepFour = observer(() => {
  const { t, i18n } = useTran();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const normalizeSelectValues = (nextValues) =>
    Array.isArray(nextValues)
      ? nextValues
          .map((item) => {
            if (typeof item === "string") return item.trim();
            if (item && typeof item === "object") {
              const resolved = item.value ?? item.key ?? item.id;
              if (typeof resolved === "string" || typeof resolved === "number")
                return String(resolved).trim();
            }
            return "";
          })
          .filter(Boolean)
      : [];

  const locationOptions = useMemo(() => {
    const options = [];
    const seenValues = new Set();

    LOCATION_OPTIONS.forEach((option) => {
      const value = option?.key;
      if (!value || seenValues.has(value)) return;
      seenValues.add(value);
      options.push({
        value,
        label: translateCountry(option.value, i18n.language),
      });
    });

    return options;
  }, [i18n.language]);

  const objectiveOptions = useMemo(
    () =>
      KEY_IMPROVEMENT.map((option) => ({
        value: option.key,
        label: translateValue(option.value, OBJECTIVE_NAME_MAP, i18n.language),
      })),
    [i18n.language],
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t("service_countries_question")}
        </label>
        <MultiSelect
          options={locationOptions}
          value={values.serviceCountries}
          onChange={(nextValues) => {
            setFieldValue(
              "serviceCountries",
              normalizeSelectValues(nextValues),
              true,
            );
            setFieldTouched("serviceCountries", true, false);
          }}
          placeholder={t("select_countries")}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t("objectives_question")}
        </label>
        <MultiSelect
          options={objectiveOptions}
          value={values.objectives}
          onChange={(nextValues) => {
            setFieldValue(
              "objectives",
              normalizeSelectValues(nextValues),
              true,
            );
            setFieldTouched("objectives", true, false);
          }}
          placeholder={t("select_objectives")}
        />
      </div>
    </div>
  );
});

export default CompanySetupStepFour;
